/**
 * @fileoverview 消息通知模块验证规则定义
 * 
 * 对应后端文件：
 * - backend/app/Http/Requests/Notification/SendNotificationRequest.php
 * - backend/app/Http/Requests/Notification/CreateTemplateRequest.php
 * - backend/app/Http/Requests/Notification/UpdateTemplateRequest.php
 * - backend/app/Http/Requests/Notification/UpdateNotificationSettingRequest.php
 * 
 * 注意事项：
 * - 验证规则字段使用 snake_case 命名（与后端 FormRequest 一致）
 * - 所有规则都需要中文注释
 * - 规则值必须与后端验证规则完全匹配
 * 
 * 更新时机：
 * - 后端 FormRequest 验证规则变更时
 * - 新增验证规则时
 * 
 * 验证方式：
 * - 对照后端 FormRequest::rules() 方法
 * - 确保验证规则完全匹配
 * 
 * @last-updated 2025-01-20
 */

import { NotificationType, NotificationLevel, NotificationChannel, TargetType } from './enums';

/**
 * 发送消息验证规则
 */
export const SEND_NOTIFICATION_VALIDATION = {
  /** 标题验证 */
  title: {
    required: true,
    type: 'string',
    maxLength: 200,
    message: '标题不能为空，最大长度200字符',
  },
  /** 内容验证 */
  content: {
    required: true,
    type: 'string',
    message: '内容不能为空',
  },
  /** 消息类型验证 */
  type: {
    required: true,
    type: 'string',
    enum: Object.values(NotificationType),
    message: '消息类型必须为有效值',
  },
  /** 消息级别验证 */
  level: {
    required: false,
    type: 'string',
    enum: Object.values(NotificationLevel),
    message: '消息级别必须为有效值',
  },
  /** 优先级验证 */
  priority: {
    required: false,
    type: 'number',
    min: 1,
    max: 5,
    message: '优先级必须在1-5之间',
  },
  /** 标签验证 */
  tags: {
    required: false,
    type: 'array',
    itemType: 'string',
    maxLength: 50,
    message: '标签必须为字符串数组，每个标签最大长度50字符',
  },
  /** 关联类型验证 */
  related_type: {
    required: false,
    type: 'string',
    maxLength: 50,
    message: '关联类型最大长度50字符',
  },
  /** 关联ID验证 */
  related_id: {
    required: false,
    type: 'number',
    message: '关联ID必须为数字',
  },
  /** 额外数据验证 */
  data: {
    required: false,
    type: 'object',
    message: '额外数据必须为对象',
  },
  /** 目标类型验证 */
  target_type: {
    required: true,
    type: 'string',
    enum: Object.values(TargetType),
    message: '目标类型必须为有效值',
  },
  /** 目标ID列表验证 */
  target_ids: {
    required: false,
    type: 'array',
    itemType: 'number',
    message: '目标ID列表必须为数字数组',
  },
  /** 发送渠道验证 */
  channels: {
    required: false,
    type: 'array',
    itemType: 'string',
    enum: Object.values(NotificationChannel),
    message: '发送渠道必须为有效值',
  },
};

/**
 * 创建消息模板验证规则
 */
export const CREATE_TEMPLATE_VALIDATION = {
  /** 事件类型验证 */
  event_type: {
    required: true,
    type: 'string',
    maxLength: 100,
    unique: true,
    message: '事件类型不能为空，最大长度100字符，且必须唯一',
  },
  /** 模板名称验证 */
  name: {
    required: true,
    type: 'string',
    maxLength: 100,
    message: '模板名称不能为空，最大长度100字符',
  },
  /** 模板描述验证 */
  description: {
    required: false,
    type: 'string',
    maxLength: 500,
    message: '模板描述最大长度500字符',
  },
  /** 标题模板验证 */
  title_template: {
    required: true,
    type: 'string',
    maxLength: 200,
    message: '标题模板不能为空，最大长度200字符',
  },
  /** 内容模板验证 */
  content_template: {
    required: true,
    type: 'string',
    message: '内容模板不能为空',
  },
  /** 短信模板代码验证 */
  sms_template_code: {
    required: false,
    type: 'string',
    maxLength: 50,
    message: '短信模板代码最大长度50字符',
  },
  /** 短信参数验证 */
  sms_params: {
    required: false,
    type: 'object',
    message: '短信参数必须为对象',
  },
  /** 邮件主题模板验证 */
  email_subject_template: {
    required: false,
    type: 'string',
    maxLength: 200,
    message: '邮件主题模板最大长度200字符',
  },
  /** 邮件内容模板验证 */
  email_content_template: {
    required: false,
    type: 'string',
    message: '邮件内容模板必须为字符串',
  },
  /** 是否启用验证 */
  is_enabled: {
    required: false,
    type: 'boolean',
    message: '是否启用必须为布尔值',
  },
};

/**
 * 更新消息模板验证规则
 */
export const UPDATE_TEMPLATE_VALIDATION = {
  /** 事件类型验证 */
  event_type: {
    required: false,
    type: 'string',
    maxLength: 100,
    unique: true,
    message: '事件类型最大长度100字符，且必须唯一',
  },
  /** 模板名称验证 */
  name: {
    required: false,
    type: 'string',
    maxLength: 100,
    message: '模板名称最大长度100字符',
  },
  /** 模板描述验证 */
  description: {
    required: false,
    type: 'string',
    maxLength: 500,
    message: '模板描述最大长度500字符',
  },
  /** 标题模板验证 */
  title_template: {
    required: false,
    type: 'string',
    maxLength: 200,
    message: '标题模板最大长度200字符',
  },
  /** 内容模板验证 */
  content_template: {
    required: false,
    type: 'string',
    message: '内容模板必须为字符串',
  },
  /** 短信模板代码验证 */
  sms_template_code: {
    required: false,
    type: 'string',
    maxLength: 50,
    message: '短信模板代码最大长度50字符',
  },
  /** 短信参数验证 */
  sms_params: {
    required: false,
    type: 'object',
    message: '短信参数必须为对象',
  },
  /** 邮件主题模板验证 */
  email_subject_template: {
    required: false,
    type: 'string',
    maxLength: 200,
    message: '邮件主题模板最大长度200字符',
  },
  /** 邮件内容模板验证 */
  email_content_template: {
    required: false,
    type: 'string',
    message: '邮件内容模板必须为字符串',
  },
  /** 是否启用验证 */
  is_enabled: {
    required: false,
    type: 'boolean',
    message: '是否启用必须为布尔值',
  },
};

/**
 * 更新消息设置验证规则
 */
export const UPDATE_SETTING_VALIDATION = {
  /** 总开关验证 */
  master_enabled: {
    required: false,
    type: 'boolean',
    message: '总开关必须为布尔值',
  },
  /** 事件类型验证 */
  event_type: {
    required: true,
    type: 'string',
    maxLength: 100,
    message: '事件类型不能为空，最大长度100字符',
  },
  /** 渠道列表验证 */
  channels: {
    required: false,
    type: 'array',
    itemType: 'string',
    enum: Object.values(NotificationChannel),
    message: '渠道列表必须为有效值',
  },
  /** 是否启用验证 */
  is_enabled: {
    required: false,
    type: 'boolean',
    message: '是否启用必须为布尔值',
  },
};
